import { Component, ElementRef, Inject, Input, OnInit, QueryList, ViewChildren, DestroyRef, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LoginService } from '../../../../core/services/login.service';
import { LoginResponse } from '../../../models/login/LoginResponse';
import { DOCUMENT, NgFor, NgClass, NgIf } from '@angular/common';
import { OtpResponse } from '../../../models/mfa/OtpResponse';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-otp-input',
    templateUrl: './otp-input.component.html',
    styleUrls: ['./otp-input.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, NgFor, NgClass, NgIf]
})
export class OtpInputComponent implements OnInit {

  @Input() otpSessionId!: string;
  @Input() challenge!: string;

  @ViewChildren('codeInput') codeInputs!: QueryList<ElementRef>;

  public isInvalidOTP = false;
  public isOtpSent = false;
  public verificationForm: FormGroup;
  public countdown = 30;
  public isCountdownActive = true;
  public loginResponse: LoginResponse;
  public otpResponse: OtpResponse;
  public isDisableState = false;

  private countdownInterval: any;
  private destroyRef = inject(DestroyRef);

  constructor(private formBuilder: FormBuilder,
     private router: Router,
     @Inject(DOCUMENT) private document: any,
     private loginService: LoginService) {
    this.loginPortalSubscriber = this.loginPortalSubscriber.bind(this);
    this.loginPortalErrSubscriber = this.loginPortalErrSubscriber.bind(this);
    this.otpSubscriber = this.otpSubscriber.bind(this);
    this.otpErrSubscriber = this.otpErrSubscriber.bind(this);
  }

  public ngOnInit() {
    this.verificationForm = this.formBuilder.group({
      code: this.formBuilder.array(Array(6).fill(''))
    });
    this.startCountdown();
  }

  public codeArray() {
    return this.verificationForm.get('code') as FormArray;
  }

  public onCodeInput(index: number, event: any) {
    const value = event.target.value;
    if (value && /^\d$/.test(value)) {
      if (index < 5) {
        this.codeInputs.get(index + 1)?.nativeElement.focus();
      }
    }
  }

  public onKeyDown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Backspace' && !this.codeArray().at(index).value && index > 0) {
      this.codeInputs.get(index - 1)?.nativeElement.focus();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      this.codeInputs.get(index - 1)?.nativeElement.focus();
    } else if (event.key === 'ArrowRight' && index < 5) {
      this.codeInputs.get(index + 1)?.nativeElement.focus();
    }
  }

  public onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text/plain').trim();
    if (pastedData && /^\d+$/.test(pastedData)) {
      const digits = pastedData.split('').slice(0, 6);
      digits.forEach((digit, index) => {
        if (index < 6) {
          this.codeArray().at(index).setValue(digit);
        }
      });
      const nextEmptyIndex = this.codeArray().value.findIndex((val: string) => !val);
      if (nextEmptyIndex !== -1) {
        this.codeInputs.get(nextEmptyIndex)?.nativeElement.focus();
      } else {
        this.codeInputs.get(5)?.nativeElement.focus();
      }
    }
  }

  public startCountdown() {
    this.countdownInterval = setInterval(() => {
      if (this.countdown > 0) {
        this.countdown--;
      } else {
        this.isCountdownActive = false;
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  public resendCode() {
    if (!this.isCountdownActive) {
      const data = {
        sessionId: this.otpSessionId
      };
      this.isOtpSent = false;
      this.loginService.mfaOtpResend(data, this.challenge)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(this.otpSubscriber, this.otpErrSubscriber);
      this.isInvalidOTP = false;
      this.isDisableState = false;
      this.countdown = 30;
      this.isCountdownActive = true;
      // Reset the OTP form
      this.codeArray().controls.forEach(control => control.setValue(''));
      this.startCountdown();
    }
  }

  public verifyCode() {
    const code = this.codeArray().value.join('');
    if (code.length === 6) {
      const formData= {
      sessionId: this.otpSessionId,
      otp: code
      }
      this.isOtpSent = false;
      this.isDisableState = true;
      this.loginService
        .mfaOtpValidate(formData, this.challenge)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(this.loginPortalSubscriber, this.loginPortalErrSubscriber);
    }
  }

  public isCodeComplete(): boolean {
    return this.codeArray().value.every((digit: string) => digit !== '');
  }

  public ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.isDisableState = false;
  }

  private loginPortalSubscriber(res: any): void {
    this.loginResponse = res;
    if (this.loginService.isRedirect(res)) {
      this.document.location.href = this.loginResponse.redirectUrl;
    }
  }

  private loginPortalErrSubscriber(err: any):void {
    this.isInvalidOTP = true;
    this.isDisableState = false;
  }

  private otpSubscriber(res: any): void {
    this.otpResponse = res;
    this.isOtpSent = this.otpResponse.sent;
  }

  private otpErrSubscriber(err: any):void {
    this.otpResponse = err;
    this.isOtpSent = false;
  }

}